import styled from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../../../formInputsV2/styles/tokens';

// v2 look for the purchase-page InfoBoxes (client summary + products table).
// Kept apart from CommonPurchase.styles.js on purpose: ClientBox there is
// still used by InfoBoxes that have not been migrated yet.

export const InfoBoxGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: ${v2Breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

export const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 16px 18px;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.border};
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(16, 24, 40, 0.06);
  font-family: ${v2FontFamily};
  color: ${v2Colors.text};

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 14px 16px;
  }
`;

export const InfoCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

export const InfoCardTitle = styled.h4`
  margin: 0;
  font-family: ${v2FontFamily};
  font-size: 17px;
  font-weight: 700;
  color: ${v2Colors.text};
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 4px 11px;
  border: 1px solid ${v2Colors.primaryBorder};
  border-radius: 999px;
  background: ${v2Colors.primarySoft};
  font-family: ${v2FontFamily};
  font-size: 12px;
  font-weight: 600;
  color: ${v2Colors.primaryHover};
  white-space: nowrap;
`;

export const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
  margin: 0;
`;

export const DetailItem = styled.div`
  min-width: 0;

  &.full {
    grid-column: 1 / -1;
  }

  dt {
    margin: 0 0 1px;
    font-size: 12px;
    font-weight: 500;
    color: ${v2Colors.textMuted};
  }

  dd {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: ${v2Colors.text};
    overflow-wrap: anywhere;
  }
`;

export const SectionLabel = styled.p`
  margin: 12px 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${v2Colors.textMuted};
`;

export const DocChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const DocChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${v2Colors.primaryBorder};
  border-radius: 999px;
  background: ${v2Colors.primarySoft};
  font-family: ${v2FontFamily};
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.2;
  color: ${v2Colors.primaryHover};
  text-align: left;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease;

  /* download icon, drawn with a mask so it follows the text colour */
  &::before {
    content: '';
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    background: currentColor;
    -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'/%3E%3Cpolyline points='7 10 12 15 17 10'/%3E%3Cline x1='12' y1='15' x2='12' y2='3'/%3E%3C/svg%3E") center / contain no-repeat;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'/%3E%3Cpolyline points='7 10 12 15 17 10'/%3E%3Cline x1='12' y1='15' x2='12' y2='3'/%3E%3C/svg%3E") center / contain no-repeat;
  }

  &:hover,
  &:focus-visible {
    background: ${v2Colors.primarySoftHover};
    border-color: ${v2Colors.primary};
  }
`;

const primaryActionStyles = `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 42px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  background: ${v2Colors.primary};
  font-family: ${v2FontFamily};
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover,
  &:focus-visible {
    background: ${v2Colors.primaryHover};
    color: #fff;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
`;

export const PrimaryActionButton = styled.button`
  ${primaryActionStyles}
`;

export const PrimaryActionLink = styled.a`
  ${primaryActionStyles}
`;

export const ProductsScroll = styled.div`
  max-height: 290px;
  overflow: auto;
  border-top: 1px solid #eaecf0;

  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 14px;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 9px 10px;
    background: ${v2Colors.inputBackground};
    border-bottom: 1px solid #d0d5dd;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: left;
    color: ${v2Colors.textMuted};
    white-space: nowrap;
  }

  td {
    padding: 10px;
    border-bottom: 1px solid #eaecf0;
    font-weight: 500;
    color: ${v2Colors.text};
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: none;
  }

  th:last-child,
  td:last-child {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td:last-child {
    font-weight: 600;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    table {
      font-size: 13px;
    }

    th,
    td {
      padding: 8px 6px;
    }
  }
`;

export const TotalRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  padding: 14px 10px 0;
  border-top: 1px solid #d0d5dd;

  span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${v2Colors.textMuted};
  }

  strong {
    font-size: 20px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${v2Colors.primary};
  }
`;
